import { useState } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'message') {
      if (value.length <= 500) {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
        setCharCount(value.length);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.message.length > 500) {
      setSubmitStatus('error');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus('idle');
    
    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4h1pkmusuhg53kqado0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setCharCount(0);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 bg-white" aria-labelledby="contact-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Contact Details */}
          <div>
            <div className="mb-6">
              <img 
                src="https://static.readdy.ai/image/afa85a1250ffb5ee259f890220a101e9/55f2f1ec535535972cbf640c41eef5f0.png" 
                alt="Contact ProEditions - Professional business writing services" 
                className="w-32 h-32 mb-6"
              />
              <h2 id="contact-heading" className="text-5xl font-light text-gray-900 mb-6">
                Contact <span className="font-bold text-orange-500">ProEditions</span>
              </h2>
            </div>
            <p className="text-2xl text-gray-600 font-light mb-6">
              We respond within 24 hours
            </p>
            <p className="text-xl text-gray-700 font-light leading-relaxed mb-8">
              We're the human touch your business deserves, and your next step towards success!
            </p>
            
            {/* Google Rating Display */}
            <a
              href="https://share.google/iGL88nfLS9D1WDS6r"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-4 bg-white border-2 border-gray-300 hover:border-orange-500 rounded-lg py-4 px-6 transition-all duration-200 cursor-pointer group focus:outline-none focus:ring-4 focus:ring-orange-500 focus:ring-opacity-50"
              aria-label="View ProEditions 5.0 star rating on Google"
            >
              <i className="ri-google-fill text-4xl text-orange-500 group-hover:scale-110 transition-transform" aria-hidden="true"></i>
              <div className="flex flex-col">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-3xl font-bold text-gray-900">5.0</span>
                  <div className="flex gap-0.5" role="img" aria-label="5 out of 5 stars">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-400 text-xl" aria-hidden="true"></i>
                    ))}
                  </div>
                </div>
                <span className="text-sm text-gray-600 group-hover:text-orange-500 transition-colors">View on Google</span>
              </div>
            </a>
          </div>

          {/* Contact Form */}
          <div className="bg-gray-50 rounded-2xl p-8 shadow-sm">
            <form 
              onSubmit={handleSubmit} 
              data-readdy-form 
              id="contact-form" 
              className="space-y-6"
              aria-label="Contact form"
            >
              <div>
                <label htmlFor="name" className="sr-only">Your Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  aria-required="true"
                  className="w-full px-4 py-3 bg-white text-gray-900 placeholder-gray-500 border border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="sr-only">Your Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  aria-required="true"
                  className="w-full px-4 py-3 bg-white text-gray-900 placeholder-gray-500 border border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors"
                />
              </div>
              
              <div>
                <label htmlFor="message" className="sr-only">Your Message</label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  placeholder="Your Message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  aria-required="true"
                  maxLength={500}
                  aria-describedby="char-count"
                  className="w-full px-4 py-3 bg-white text-gray-900 placeholder-gray-500 border border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none transition-colors"
                />
                <div id="char-count" className="text-right text-sm text-gray-500 mt-2" aria-live="polite">
                  {charCount}/500 characters
                </div>
              </div>
              
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-4 px-6 rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-4 focus:ring-orange-500 focus:ring-opacity-50"
                  aria-label={isSubmitting ? 'Sending message...' : 'Send message to ProEditions'}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </div>
              
              {submitStatus === 'success' && (
                <div 
                  className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm"
                  role="alert"
                  aria-live="polite"
                >
                  Thank you! We'll get back to you soon.
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div 
                  className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm"
                  role="alert"
                  aria-live="assertive"
                >
                  {formData.message.length > 500 
                    ? 'Message exceeds 500 characters. Please shorten your message.'
                    : 'Something went wrong. Please try again.'}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
